import { useEffect } from "react";
import { XMarkIcon } from "../assets/icons/index.js";
import cyclesOfLifeDark from "../assets/images/cycles-of-life-dark.svg";
import cyclesOfLifeLight from "../assets/images/cycles-of-life-light.svg";
import planetsSvg from "../assets/images/planets.svg";
import { useGlobalContext } from "../context/GlobalContext.jsx";
import { getCycleForSlot } from "./helpers/CyclesOfLife/cyclePeriods.js";
import {
  initializeUI,
  showWeeklyOverviewCycle,
} from "./helpers/CyclesOfLife/cycles-of-life.js";
import BorderBox from "./BorderBox.jsx";

const CyclesOfLifeModal = ({ scaleIn, onClose, modalRef }) => {
  const { darkMode } = useGlobalContext();
  const cyclesOfLife = darkMode ? cyclesOfLifeDark : cyclesOfLifeLight;

  useEffect(() => {
    const timer = setTimeout(() => {
      initializeUI();
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      className={`w-full h-full transition-all duration-300 ease-in-out outline-none ${
        scaleIn ? "opacity-100 scale-100" : "opacity-0 scale-95"
      }`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="cycles-modal-title"
      ref={modalRef}
      tabIndex={-1}
    >
      <BorderBox overflow noPadding className="relative">
        {/* Title */}
        <div className="absolute top-3 left-1/2 transform -translate-x-1/2 text-lz-candle-dim dark:text-lz-concrete text-lg font-semibold z-10 text-center uppercase">
          Cycles of Life
        </div>

        {/* Close button */}
        <button
          type="button"
          className="absolute top-8 right-8 p-3 rounded-lg bg-lz-paper/70 hover:bg-lz-paper text-lz-ink-mid dark:bg-white/20 dark:hover:bg-white/40 dark:text-white transition-all duration-200 z-50 shadow-lg"
          onClick={onClose}
          tabIndex={0}
          aria-label="Close Cycles of Life"
        >
          <XMarkIcon className="h-8 w-8" />
        </button>

        {/* Cycles of Life Wheel */}
        <div className="relative bg-lz-paper-deep dark:bg-lz-ink-mid overflow-hidden">
          <div className="py-4 max-w-4xl mx-auto mt-12 relative z-10">
            <div className="text-center">
              <div className="text-center max-w-xl mx-auto">
                <object
                  id="cyclesOfLifeModal"
                  width="100%"
                  type="image/svg+xml"
                  data={cyclesOfLife}
                ></object>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t-4 border-lz-candle-dim bg-white dark:bg-lz-charcoal">
          <div className="max-w-4xl mx-auto">
            {/* Tabs Container */}
            <div id="tabsContainer">
              <div className="container relative mx-auto md:my-8 px-2 md:px-6 select-none">
                {/* Tabs Flex Container */}
                <div className="flex flex-col justify-center w-full mx-auto mb-4 md:mb-8 md:space-x-10 md:flex-row">
                  {/* Tab 1 */}
                  <div
                    className="flex justify-center text-center text-lz-ink-mid dark:text-lz-concrete border-b md:border-b-0 hover:text-lz-ember dark:hover:text-lz-candle-dim md:w-1/3 cycles-tab cursor-pointer"
                    data-target="panel-1"
                  >
                    <div
                      className="py-2 md:py-4 border-b-4 text-xl border-lz-ember dark:border-lz-candle-dim"
                      data-target="panel-1"
                    >
                      Cycle Description
                    </div>
                  </div>
                  {/* Tab 2 */}
                  <div
                    className="flex justify-center text-center text-lz-ink-mid dark:text-lz-concrete border-b md:border-b-0 hover:text-lz-ember dark:hover:text-lz-candle-dim md:w-1/3 cycles-tab cursor-pointer"
                    data-target="panel-2"
                  >
                    <div className="py-2 md:py-4 text-xl" data-target="panel-2">
                      Weekly Overview
                    </div>
                  </div>
                  {/* Tab 3 */}
                  <div
                    className="flex justify-center text-center text-lz-ink-mid dark:text-lz-concrete border-b md:border-b-0 hover:text-lz-ember dark:hover:text-lz-candle-dim md:w-1/3 cycles-tab cursor-pointer"
                    data-target="panel-3"
                  >
                    <div className="py-2 md:py-4 text-xl" data-target="panel-3">
                      Background
                    </div>
                  </div>
                </div>

                {/* Tab Panels */}
                <div id="panelsContainer" className="container mx-auto pb-8">
                  {/* Panel 1 */}
                  <div className="cycles-panel panel-1 py-4">
                    <div className="flex justify-between text-lz-candle-dim py-2 border-b-2 border-lz-candle-dim">
                      <div className="flex">
                        <div className="w-24">
                          <object
                            id="planets"
                            width="60%"
                            type="image/svg+xml"
                            data={planetsSvg}
                          ></object>
                        </div>
                        <div>
                          <div
                            className="text-3xl md:text-4xl font-semibold"
                            id="periodTitle"
                          ></div>
                          <div className="flex flex-wrap gap-x-3 text-xl md:text-2xl text-lz-candle-dim/80">
                            <span id="cycleRange"></span>
                            <span id="periodTime"></span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          id="btnPrevCycle"
                          className="px-1 md:px-2 py-1 md:m-1 bg-lz-concrete text-lz-ink-mid hover:bg-lz-ember hover:text-white dark:bg-lz-ink-mid dark:text-lz-concrete dark:hover:bg-lz-candle-dim dark:hover:text-lz-ink text-xs font-bold border border-lz-paper-border dark:border-lz-candle-dim/50 rounded-md cursor-pointer select-none"
                        >
                          &#x2190;
                        </button>
                        <button
                          id="btnNow"
                          className="px-1 md:px-2 py-1 md:m-1 bg-lz-concrete text-lz-ink-mid hover:bg-lz-ember hover:text-white dark:bg-lz-ink-mid dark:text-lz-concrete dark:hover:bg-lz-candle-dim dark:hover:text-lz-ink text-xs font-bold border border-lz-paper-border dark:border-lz-candle-dim/50 rounded-md cursor-pointer select-none"
                        >
                          NOW
                        </button>
                        <button
                          id="btnNextCycle"
                          className="px-1 md:px-2 py-1 md:m-1 bg-lz-concrete text-lz-ink-mid hover:bg-lz-ember hover:text-white dark:bg-lz-ink-mid dark:text-lz-concrete dark:hover:bg-lz-candle-dim dark:hover:text-lz-ink text-xs font-bold border border-lz-paper-border dark:border-lz-candle-dim/50 rounded-md cursor-pointer select-none"
                        >
                          &#x2192;
                        </button>
                      </div>
                    </div>
                    <div className="mt-8">
                      <h2 className="text-2xl text-lz-ink-mid dark:text-lz-concrete mb-4">
                        Summary
                      </h2>
                      <div
                        className="text-left text-lz-ink-mid dark:text-lz-concrete md:text-2xl space-y-4 whitespace-pre-line"
                        id="periodSummary"
                      ></div>
                    </div>
                    <div className="mt-8">
                      <h2 className="text-2xl text-lz-ink-mid dark:text-lz-concrete mb-4">
                        Description
                      </h2>
                      <div
                        className="text-left text-xl text-lz-ink-mid dark:text-lz-concrete space-y-4 whitespace-pre-line"
                        id="periodDescription"
                      ></div>
                    </div>
                  </div>

                  {/* Panel 2 */}
                  <div className="md:py-4 hidden cycles-panel panel-2">
                    <div className="mb-10">
                      <p className="hidden md:block text-xl text-lz-ink-mid dark:text-lz-concrete">
                        The daily cycle divides the 24 hours of the day into
                        seven periods. Each period consists of approximately 3
                        hours and 26 minutes. The daily cycle begins at midnight
                        and ends at midnight; noon of each day is the center of
                        the cycle.
                      </p>
                    </div>

                    <table className="table-auto text-xs md:text-base border-separate border-spacing-1 w-full text-lz-ink-mid dark:text-lz-concrete">
                      <thead>
                        <tr className="text-base md:hidden">
                          <th></th>
                          <th>
                            <button
                              id="btn-weekday-0"
                              className="px-1 md:px-2 py-1 border border-lz-paper-border dark:border-lz-candle-dim/50 rounded-md text-base font-normal text-lz-ink-mid dark:text-lz-concrete bg-lz-concrete dark:bg-lz-ink-mid hover:bg-lz-ember hover:text-white dark:hover:bg-lz-candle-dim dark:hover:text-lz-ink cursor-pointer select-none"
                            >
                              Sun
                            </button>
                          </th>
                          <th>
                            <button
                              id="btn-weekday-1"
                              className="px-1 md:px-2 py-1 border border-lz-paper-border dark:border-lz-candle-dim/50 rounded-md text-base font-normal text-lz-ink-mid dark:text-lz-concrete bg-lz-concrete dark:bg-lz-ink-mid hover:bg-lz-ember hover:text-white dark:hover:bg-lz-candle-dim dark:hover:text-lz-ink cursor-pointer select-none"
                            >
                              Mon
                            </button>
                          </th>
                          <th>
                            <button
                              id="btn-weekday-2"
                              className="px-1 md:px-2 py-1 border border-lz-paper-border dark:border-lz-candle-dim/50 rounded-md text-base font-normal text-lz-ink-mid dark:text-lz-concrete bg-lz-concrete dark:bg-lz-ink-mid hover:bg-lz-ember hover:text-white dark:hover:bg-lz-candle-dim dark:hover:text-lz-ink cursor-pointer select-none"
                            >
                              Tue
                            </button>
                          </th>
                          <th>
                            <button
                              id="btn-weekday-3"
                              className="px-1 md:px-2 py-1 border border-lz-paper-border dark:border-lz-candle-dim/50 rounded-md text-base font-normal text-lz-ink-mid dark:text-lz-concrete bg-lz-concrete dark:bg-lz-ink-mid hover:bg-lz-ember hover:text-white dark:hover:bg-lz-candle-dim dark:hover:text-lz-ink cursor-pointer select-none"
                            >
                              Wed
                            </button>
                          </th>
                          <th>
                            <button
                              id="btn-weekday-4"
                              className="px-1 md:px-2 py-1 border border-lz-paper-border dark:border-lz-candle-dim/50 rounded-md text-base font-normal text-lz-ink-mid dark:text-lz-concrete bg-lz-concrete dark:bg-lz-ink-mid hover:bg-lz-ember hover:text-white dark:hover:bg-lz-candle-dim dark:hover:text-lz-ink cursor-pointer select-none"
                            >
                              Thu
                            </button>
                          </th>
                          <th>
                            <button
                              id="btn-weekday-5"
                              className="px-1 md:px-2 py-1 border border-lz-paper-border dark:border-lz-candle-dim/50 rounded-md text-base font-normal text-lz-ink-mid dark:text-lz-concrete bg-lz-concrete dark:bg-lz-ink-mid hover:bg-lz-ember hover:text-white dark:hover:bg-lz-candle-dim dark:hover:text-lz-ink cursor-pointer select-none"
                            >
                              Fri
                            </button>
                          </th>
                          <th>
                            <button
                              id="btn-weekday-6"
                              className="px-1 md:px-2 py-1 border border-lz-paper-border dark:border-lz-candle-dim/50 rounded-md text-base font-normal text-lz-ink-mid dark:text-lz-concrete bg-lz-concrete dark:bg-lz-ink-mid hover:bg-lz-ember hover:text-white dark:hover:bg-lz-candle-dim dark:hover:text-lz-ink cursor-pointer select-none"
                            >
                              Sat
                            </button>
                          </th>
                        </tr>
                        <tr className="text-sm hidden md:table-row">
                          <th></th>
                          <th>
                            <button
                              id="btn-weekday-10"
                              className="px-1 md:px-2 py-1 border border-lz-paper-border dark:border-lz-candle-dim/50 rounded-md text-base font-normal text-lz-ink-mid dark:text-lz-concrete bg-lz-concrete dark:bg-lz-ink-mid hover:bg-lz-ember hover:text-white dark:hover:bg-lz-candle-dim dark:hover:text-lz-ink cursor-pointer select-none"
                            >
                              Sunday
                            </button>
                          </th>
                          <th>
                            <button
                              id="btn-weekday-11"
                              className="px-1 md:px-2 py-1 border border-lz-paper-border dark:border-lz-candle-dim/50 rounded-md text-base font-normal text-lz-ink-mid dark:text-lz-concrete bg-lz-concrete dark:bg-lz-ink-mid hover:bg-lz-ember hover:text-white dark:hover:bg-lz-candle-dim dark:hover:text-lz-ink cursor-pointer select-none"
                            >
                              Monday
                            </button>
                          </th>
                          <th>
                            <button
                              id="btn-weekday-12"
                              className="px-1 md:px-2 py-1 border border-lz-paper-border dark:border-lz-candle-dim/50 rounded-md text-base font-normal text-lz-ink-mid dark:text-lz-concrete bg-lz-concrete dark:bg-lz-ink-mid hover:bg-lz-ember hover:text-white dark:hover:bg-lz-candle-dim dark:hover:text-lz-ink cursor-pointer select-none"
                            >
                              Tuesday
                            </button>
                          </th>
                          <th>
                            <button
                              id="btn-weekday-13"
                              className="px-1 md:px-2 py-1 border border-lz-paper-border dark:border-lz-candle-dim/50 rounded-md text-base font-normal text-lz-ink-mid dark:text-lz-concrete bg-lz-concrete dark:bg-lz-ink-mid hover:bg-lz-ember hover:text-white dark:hover:bg-lz-candle-dim dark:hover:text-lz-ink cursor-pointer select-none"
                            >
                              Wednesday
                            </button>
                          </th>
                          <th>
                            <button
                              id="btn-weekday-14"
                              className="px-1 md:px-2 py-1 border border-lz-paper-border dark:border-lz-candle-dim/50 rounded-md text-base font-normal text-lz-ink-mid dark:text-lz-concrete bg-lz-concrete dark:bg-lz-ink-mid hover:bg-lz-ember hover:text-white dark:hover:bg-lz-candle-dim dark:hover:text-lz-ink cursor-pointer select-none"
                            >
                              Thursday
                            </button>
                          </th>
                          <th>
                            <button
                              id="btn-weekday-15"
                              className="px-1 md:px-2 py-1 border border-lz-paper-border dark:border-lz-candle-dim/50 rounded-md text-base font-normal text-lz-ink-mid dark:text-lz-concrete bg-lz-concrete dark:bg-lz-ink-mid hover:bg-lz-ember hover:text-white dark:hover:bg-lz-candle-dim dark:hover:text-lz-ink cursor-pointer select-none"
                            >
                              Friday
                            </button>
                          </th>
                          <th>
                            <button
                              id="btn-weekday-16"
                              className="px-1 md:px-2 py-1 border border-lz-paper-border dark:border-lz-candle-dim/50 rounded-md text-base font-normal text-lz-ink-mid dark:text-lz-concrete bg-lz-concrete dark:bg-lz-ink-mid hover:bg-lz-ember hover:text-white dark:hover:bg-lz-candle-dim dark:hover:text-lz-ink cursor-pointer select-none"
                            >
                              Saturday
                            </button>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {PERIOD_TIMES.map((time, slot) => (
                          <tr key={slot} className="text-xl">
                            <td className="text-sm text-center border bg-lz-concrete dark:bg-lz-ink-mid leading-tight p-1">
                              No. {slot + 1}
                              <br />
                              <span className="text-xs">{time}</span>
                            </td>
                            {WEEKDAYS.map((weekday, day) => {
                              const letter = CYCLE_LETTERS[getCycleForSlot(day, slot)];
                              return (
                                <td
                                  key={weekday}
                                  className="text-center border bg-lz-concrete dark:bg-lz-ink-mid p-0"
                                >
                                  <button
                                    type="button"
                                    className="w-full py-2 cursor-pointer select-none hover:bg-lz-ember hover:text-white dark:hover:bg-lz-candle-dim dark:hover:text-lz-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-lz-ember dark:focus-visible:outline-lz-candle-dim"
                                    aria-label={`Cycle ${letter} (${weekday}, period ${slot + 1}): show description`}
                                    onClick={() => showWeeklyOverviewCycle(day, slot)}
                                  >
                                    {letter}
                                  </button>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Panel 3 */}
                  <div className="py-4 hidden cycles-panel panel-3">
                    <p className="my-4 md:text-2xl text-lz-ink-mid dark:text-lz-concrete">
                      The following is an excerpt from an article in The
                      Rosicrucian Digest written by the Research Department of
                      Rose-Croix University (published August 1982):
                    </p>
                    <p className="my-4 text-xl text-lz-ink-mid dark:text-lz-concrete">
                      Many persons benefited from the book, Self Mastery and
                      Fate With the Cycles of Life, by Dr. H. Spencer Lewis.
                      This book explains some of the cycles of life and their
                      uses. A study of the cycles in this book reveals
                      interesting parallels with cycles found in ancient Hindu
                      astronomy.
                    </p>
                    <p className="my-4 text-xl text-lz-ink-mid dark:text-lz-concrete">
                      Shown here is the most commonly used cycle in Dr.
                      Lewis&apos; book. It concerns the seven days of the week
                      and the seven daily cycles. Dr. Lewis used the white keys
                      of the piano to show the seven daily periods. The first
                      period of the first day, Sunday, is called G. If the right
                      thumb is set on the note G, the first finger will rest on
                      A, the second on B, the third on C, and the fourth on D.
                      The sixth note is E and the seventh note is F. This counts
                      an octave and describes the seven daily periods using the
                      white notes of the piano.
                    </p>
                    <p className="my-4 text-xl text-lz-ink-mid dark:text-lz-concrete">
                      Starting the second day of the week, Monday, note that the
                      first period is C and not G. To obtain the first cycle in
                      each day of the week, start on G for the first day and
                      count three notes: A=1, B=2, and C=3 to obtain the first
                      period of the second day, Monday. The first period of the
                      third day, Tuesday, is obtained by starting on C and
                      counting three notes: D=1, E=2, and F=3. In this manner,
                      we determine B as the first period of the fourth day,
                      Wednesday; E the first period of the fifth day, Thursday;
                      A as the first period of the sixth day, Friday; and D as
                      the first period of the seventh day, Saturday.
                    </p>
                    <p className="my-4 text-xl text-lz-ink-mid dark:text-lz-concrete">
                      In Dr. Lewis&apos; system, the second period of each day
                      (which includes the first or sunrise hour of the Hindus)
                      corresponds to the planet which rules that particular day.
                      Dr. Lewis gave us A as the second period of the first day,
                      which, according to the Hindus, meant that the fourth
                      planet, the Sun, rules that day as well as the second
                      period or sunrise time of that day. Similarly, the second
                      period of the second day is D which corresponds to the
                      Moon or seventh planet, so we say the Moon rules Monday
                      and the second period of Monday. Similarly, Mars rules the
                      third day, Tuesday, and the 2nd period of that day;
                      Mercury rules Wednesday and the second period of that day;
                      Jupiter rules Thursday; Venus rules Friday; and Saturn
                      rules Saturday.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </BorderBox>
    </div>
  );
};

const CYCLE_LETTERS = "ABCDEFG";

const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

// Weekly Overview row labels, one per daily period (slot 0–6)
const PERIOD_TIMES = [
  "Midnight to 3:25\u00a0a.m.",
  "3:25 a.m. to 6:51\u00a0a.m.",
  "6:51 a.m. to 10:17\u00a0a.m.",
  "10:17 a.m. to 1:42\u00a0p.m.",
  "1:42 p.m. to 5:08\u00a0p.m.",
  "5:08 p.m. to 8:34\u00a0p.m.",
  "8:34 p.m. to midnight",
];

export default CyclesOfLifeModal;
